/**
 * http.js — Instance Axios centralisée pour toutes les requêtes API
 *
 * ✅ Ajoute automatiquement le token Authorization (lu au moment de chaque requête)
 * ✅ Intercepte les 401 pour refresh token + forceLogout (mutex anti-boucle)
 * ✅ Source unique de vérité pour les appels HTTP — NE PAS passer d'en-tête
 *    Authorization manuellement dans les appel http.get/post/put/delete.
 * ✅ Cache mémoire GET avec TTL pour réduire la latence sur les menus principaux
 *
 * Usage :
 *   import http from '../api/http';
 *   const res = await http.get(`${API}/clients`);
 *
 * Invalidation cache :
 *   import { invalidateCache } from '../api/http';
 *   invalidateCache('/clients');      // invalide une entrée
 *   invalidateCache();                // vide tout le cache
 */
import axios from 'axios';
import { broadcastLogout, forceLogout } from '../auth/authSync';

// ─── URL Backend (même logique que App.js) ────────────────────
const BACKEND_URL = (() => {
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (envUrl.startsWith('https://')) return envUrl;
  const host = (typeof window !== 'undefined') ? window.location.hostname : 'localhost';
  return `http://${host}:8001`;
})();

const API = `${BACKEND_URL}/api`;

// ─── Endpoints qui n'ont PAS besoin d'Authorization ──────────
// /auth/reset-password : whitelisté car il utilise le token de récupération
// Supabase (pas le token de session normal) — voir ResetPasswordPage.
const AUTH_WHITELIST = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/reset-password'];

// ─── Cache GET mémoire avec TTL ───────────────────────────────
// Clé = URL complète, valeur = { data, expires, timestamp }
const _cache = new Map();

// TTL par pattern d'URL (en millisecondes)
// staleTtl = durée pendant laquelle les données périmées sont retournées instantanément
//            (stale-while-revalidate) pendant qu'un refresh s'exécute en arrière-plan
const CACHE_TTL_RULES = [
  { pattern: /\/catalog\/products/, ttl: 10 * 60 * 1000, staleTtl: 60 * 60 * 1000 }, // 10m fresh, 1h stale
  { pattern: /\/clients(\?|$)/,     ttl: 3 * 60 * 1000,  staleTtl: 30 * 60 * 1000 }, // 3m fresh, 30m stale
  { pattern: /\/quotes(\?|$)/,      ttl: 60 * 1000,       staleTtl: 10 * 60 * 1000 }, // 1m fresh, 10m stale
  { pattern: /\/worksites(\?|$)/,   ttl: 60 * 1000,       staleTtl: 10 * 60 * 1000 }, // 1m fresh, 10m stale
  { pattern: /\/materials(\?|$)/,   ttl: 3 * 60 * 1000,  staleTtl: 30 * 60 * 1000 }, // 3m fresh, 30m stale
  { pattern: /\/schedules(\?|$)/,   ttl: 2 * 60 * 1000,  staleTtl: 10 * 60 * 1000 }, // 2m fresh, 10m stale
  { pattern: /\/searches(\?|$)/,    ttl: 2 * 60 * 1000,  staleTtl: 10 * 60 * 1000 }, // 2m fresh, 10m stale
  { pattern: /\/users\/me(\?|$)/,   ttl: 5 * 60 * 1000,  staleTtl: 60 * 60 * 1000 }, // 5m fresh, 1h stale
  { pattern: /\/team-leaders/,      ttl: 5 * 60 * 1000,  staleTtl: 30 * 60 * 1000 }, // 5m fresh, 30m stale
  { pattern: /\/collaborators/,     ttl: 5 * 60 * 1000,  staleTtl: 30 * 60 * 1000 }, // 5m fresh, 30m stale
  { pattern: /\/stats\/dashboard/,  ttl: 60 * 1000,       staleTtl: 5 * 60 * 1000  }, // 1m fresh, 5m stale
];

const getCacheTtl = (url) => {
  for (const rule of CACHE_TTL_RULES) {
    if (rule.pattern.test(url)) return { ttl: rule.ttl, staleTtl: rule.staleTtl };
  }
  return { ttl: 0, staleTtl: 0 };
};

// Export pour invalidation depuis les composants après mutation
export const invalidateCache = (urlPattern) => {
  if (!urlPattern) {
    _cache.clear();
    return;
  }
  for (const key of _cache.keys()) {
    if (key.includes(urlPattern)) _cache.delete(key);
  }
};

// ─── Mise à jour optimiste du cache (évite refetch complet) ──
// Permet de mettre à jour une entrée dans une liste en cache après mutation.
// Ex: updateCacheItem('/clients', clientId, { nom: 'Nouveau Nom' })
export const updateCacheItem = (urlPattern, itemId, updates) => {
  for (const [key, entry] of _cache.entries()) {
    if (!key.includes(urlPattern)) continue;
    const data = entry.data;
    // Gère les réponses { data: [...] } ou directement [...]
    const items = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : null);
    if (!items) continue;
    const idx = items.findIndex(item => item.id === itemId);
    if (idx !== -1) {
      items[idx] = { ...items[idx], ...updates };
    }
  }
};

// ─── Ajout optimiste d'un item au cache (évite refetch complet) ──
export const addCacheItem = (urlPattern, newItem) => {
  for (const [key, entry] of _cache.entries()) {
    if (!key.includes(urlPattern)) continue;
    const data = entry.data;
    const items = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : null);
    if (!items) continue;
    // Éviter les doublons
    if (!items.some(item => item.id === newItem.id)) {
      items.unshift(newItem);
    }
  }
};

// ─── Suppression optimiste d'un item du cache ──
export const removeCacheItem = (urlPattern, itemId) => {
  for (const [key, entry] of _cache.entries()) {
    if (!key.includes(urlPattern)) continue;
    const data = entry.data;
    const items = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : null);
    if (!items) continue;
    const idx = items.findIndex(item => item.id === itemId);
    if (idx !== -1) {
      items.splice(idx, 1);
    }
  }
};

// ─── Lecture synchrone du cache (pour initialisation composants) ──
// Retourne les données en cache si elles existent (même périmées mais dans le staleTtl).
// Permet aux composants d'initialiser leur state sans attendre un await.
export const getCached = (url) => {
  // Correspondance exacte d'abord
  const exact = _cache.get(url);
  if (exact) {
    const { staleTtl } = getCacheTtl(url);
    if (staleTtl > 0 && Date.now() < exact.timestamp + staleTtl) return exact.data;
    if (Date.now() < exact.expires) return exact.data;
  }
  return null;
};

// ─── Prefetch — pré-charger les données pour navigation instantanée ──
// Appelé sur hover des onglets de navigation. Ne fait rien si le cache est frais.
const _prefetchInFlight = new Set();
export const prefetch = (...urls) => {
  urls.forEach(url => {
    if (_prefetchInFlight.has(url)) return;
    const { ttl } = getCacheTtl(url);
    if (ttl > 0) {
      const cached = _cache.get(url);
      if (cached && Date.now() < cached.expires) return; // Déjà frais
    }
    _prefetchInFlight.add(url);
    http.get(url).catch(() => {}).finally(() => _prefetchInFlight.delete(url));
  });
};

// ─── Déduplication des requêtes GET en vol ────────────────────
// Si 2 composants appellent GET /clients simultanément, une seule requête part
const _pendingRequests = new Map();

export const deduplicatedGet = (url, config) => {
  const key = url + JSON.stringify(config?.params || {});
  if (_pendingRequests.has(key)) {
    return _pendingRequests.get(key);
  }
  const promise = http.get(url, config).finally(() => {
    _pendingRequests.delete(key);
  });
  _pendingRequests.set(key, promise);
  return promise;
};

// ─── Instance Axios ───────────────────────────────────────────
const http = axios.create({
  timeout: 60000, // 60s (était 30s — marge pour démarrage lent du backend)
});

// ─── Request interceptor : cache GET + injection token ───────
http.interceptors.request.use(
  (config) => {
    // Ne pas injecter de token sur les routes publiques
    const isPublic = AUTH_WHITELIST.some(path => config.url?.includes(path));
    if (!isPublic) {
      // TOUJOURS lire depuis localStorage au moment de la requête (jamais en closure)
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Cache GET : stale-while-revalidate
    if ((config.method === 'get' || !config.method) && !config._isRevalidation) {
      const { ttl, staleTtl } = getCacheTtl(config.url || '');
      if (ttl > 0) {
        const cacheKey = config.url + (config.params ? JSON.stringify(config.params) : '');
        const cached = _cache.get(cacheKey);
        if (cached) {
          const now = Date.now();
          if (now < cached.expires) {
            // ✅ Cache FRAIS — retourner immédiatement
            config.adapter = () => Promise.resolve({
              data: cached.data,
              status: 200,
              statusText: 'OK (cached)',
              headers: {},
              config,
              request: {},
            });
          } else if (now < cached.timestamp + staleTtl) {
            // 🔄 Cache PÉRIMÉ mais dans la fenêtre stale — retourner stale + revalider en fond
            config.adapter = () => Promise.resolve({
              data: cached.data,
              status: 200,
              statusText: 'OK (stale)',
              headers: {},
              config,
              request: {},
            });
            // Revalidation silencieuse en arrière-plan
            setTimeout(() => {
              http.get(config.url, {
                ...(config.params ? { params: config.params } : {}),
                _isRevalidation: true,
                _cacheKey: cacheKey,
                _cacheTtl: ttl,
              }).catch(() => {});
            }, 0);
          } else {
            // ❌ Cache trop vieux — requête normale
            config._cacheKey = cacheKey;
            config._cacheTtl = ttl;
          }
        } else {
          // Pas de cache — requête normale
          config._cacheKey = cacheKey;
          config._cacheTtl = ttl;
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response interceptor : stockage cache + retry + 401 ─────
let _logoutTriggered = false;
let _refreshPromise = null;  // Mutex : une seule tentative de refresh à la fois

// Extrait le segment de base d'une URL (ex: "/api/quotes/123" → "/quotes")
const _getUrlBase = (url = '') => {
  const match = url.match(/\/api(\/[a-z_]+)/i);
  return match ? match[1] : null;
};

http.interceptors.response.use(
  (response) => {
    const method = response.config.method?.toLowerCase();

    if (method === 'get') {
      // Stocker en cache si la requête le demandait
      if (response.config._cacheKey && response.config._cacheTtl) {
        _cache.set(response.config._cacheKey, {
          data: response.data,
          timestamp: Date.now(),
          expires: Date.now() + response.config._cacheTtl,
        });
      }
    } else if (['post', 'put', 'patch', 'delete'].includes(method)) {
      // Toute mutation invalide automatiquement le cache du même endpoint
      const base = _getUrlBase(response.config.url);
      if (base) {
        for (const key of _cache.keys()) {
          if (key.includes(base)) _cache.delete(key);
        }
      }
      if (['/schedules', '/worksites', '/planning', '/mission'].includes(base)) {
        for (const key of _cache.keys()) {
          if (/\/(schedules|worksites|planning|technicians|mission-reports)/.test(key)) _cache.delete(key);
        }
      }
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Retry automatique (max 2 fois) sur erreurs réseau transitoires
    // Couvre: timeout, perte réseau, ERR_CONTENT_LENGTH_MISMATCH (LAN/WiFi)
    const isRetryableError = (
      error.code === 'ECONNABORTED' ||
      error.code === 'ERR_NETWORK' ||
      error.code === 'ERR_BAD_RESPONSE' ||
      error.message?.toLowerCase().includes('content length') ||
      error.message?.toLowerCase().includes('network error')
    );
    const retryCount = originalRequest._retryCount || 0;
    // Les GET/HEAD sont toujours sûrs à rejouer. Les uploads de photos (POST .../photos)
    // et la mise à jour d'une recherche existante (PATCH .../searches/{id}) sont aussi
    // rejoués : ce sont des opérations idempotentes (remplacement des mêmes champs /
    // dédoublonnage par nom de fichier), un retry ne crée donc pas de doublon visible.
    // Note : on NE rejoue PAS un POST /searches/draft (création), qui créerait un doublon
    // si la 1ère tentative avait en réalité réussi côté serveur.
    const isSafeMethod = ['get', 'head'].includes(originalRequest.method?.toLowerCase());
    const isPhotoUpload = originalRequest.method?.toLowerCase() === 'post' && /\/photos(\?|$)/.test(originalRequest.url || '');
    const isSearchUpdate = originalRequest.method?.toLowerCase() === 'patch' && /\/searches\/[^/]+(\?|$)/.test(originalRequest.url || '');
    if (isRetryableError && (isSafeMethod || isPhotoUpload || isSearchUpdate) && retryCount < 2) {
      originalRequest._retryCount = retryCount + 1;
      // Back-off linéaire : 1s, puis 2s
      const delay = (retryCount + 1) * 1000;
      console.warn(`[http] Retry ${retryCount + 1}/2 pour ${originalRequest.url} (${error.code || error.message})`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return http(originalRequest);
    }

    // Compte désactivé par un ADMIN (détecté sur un token déjà en session) → logout immédiat
    if (error.response?.status === 403 && error.response?.data?.detail?.code === 'account_deactivated' && !_logoutTriggered) {
      _logoutTriggered = true;
      broadcastLogout('account_deactivated');
      forceLogout('account_deactivated');
      setTimeout(() => { _logoutTriggered = false; }, 8000);
      return Promise.reject(error);
    }

    // Pas un 401, ou déjà retry, ou logout déjà déclenché → passer
    if (error.response?.status !== 401 || originalRequest._retry || _logoutTriggered) {
      return Promise.reject(error);
    }

    // Route publique (login/register) → 401 = mauvais identifiants, pas un token expiré
    const isPublic = AUTH_WHITELIST.some(path => originalRequest.url?.includes(path));
    if (isPublic) {
      return Promise.reject(error);
    }

    // Pas de token en localStorage → tenter un refresh si refresh_token existe
    if (!localStorage.getItem('token')) {
      const refreshTokenCheck = localStorage.getItem('refresh_token');
      if (!refreshTokenCheck) {
        // Aucun token, aucun refresh_token → logout propre
        if (!_logoutTriggered) {
          _logoutTriggered = true;
          console.warn('[http] Pas de token ni refresh_token — forceLogout');
          broadcastLogout('no_token');
          forceLogout('no_token');
          setTimeout(() => { _logoutTriggered = false; }, 8000);
        }
        return Promise.reject(error);
      }
      // refresh_token existe → on continue vers la logique de refresh ci-dessous
    }

    originalRequest._retry = true;

    // Tenter le refresh (avec mutex pour éviter N refresh simultanés)
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        // Si un refresh est déjà en cours, attendre son résultat
        if (!_refreshPromise) {
          _refreshPromise = axios.post(`${API}/auth/refresh`, {
            refresh_token: refreshToken
          }).finally(() => { _refreshPromise = null; });
        }

        const response = await _refreshPromise;
        const { access_token, refresh_token: newRefreshToken } = response.data;

        if (!access_token) {
          throw new Error('Refresh response missing access_token');
        }

        // Mettre à jour les tokens dans localStorage (source unique de vérité)
        localStorage.setItem('token', access_token);
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }

        // Retry la requête originale — l'intercepteur request relira le token frais
        // On supprime l'ancien Authorization pour laisser l'intercepteur le réinjecter
        delete originalRequest.headers['Authorization'];
        originalRequest._retry = true;
        return http(originalRequest);
      } catch (refreshError) {
        // Refresh échoué → logout une seule fois
        if (!_logoutTriggered) {
          _logoutTriggered = true;
          console.warn('[http] Refresh token échoué — forceLogout');
          broadcastLogout('refresh_failed');
          forceLogout('refresh_failed');
          setTimeout(() => { _logoutTriggered = false; }, 8000);
        }
        return Promise.reject(refreshError);
      }
    }

    // Pas de refresh token → logout une seule fois
    if (!_logoutTriggered) {
      _logoutTriggered = true;
      console.warn('[http] Pas de refresh_token — forceLogout');
      broadcastLogout('token_expired');
      forceLogout('token_expired');
      setTimeout(() => { _logoutTriggered = false; }, 8000);
    }
    return Promise.reject(error);
  }
);

/**
 * Réinitialise les flags internes après un login réussi.
 * À appeler APRÈS avoir stocké le nouveau token dans localStorage.
 */
export function resetHttpAuth() {
  _logoutTriggered = false;
  _refreshPromise = null;
}

export { http, API, BACKEND_URL };
export default http;
